/**
 * Маппер для преобразования Client между API и доменной моделью
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { V1Client, V1Contact } from '@/api/clients/client/models'
import { V1Currency, V1TimezoneSource } from '@/api/clients/client/models'
import {
  ContactType,
  type Client,
  type Contact,
  type CurrencyCode,
  type TimezoneSource,
} from '../types/client.types'

// DEV-2478: источник пояса из client-data. INVALID — пояс неизвестен, источника нет.
const TIMEZONE_SOURCES: Partial<Record<string, TimezoneSource>> = {
  [V1TimezoneSource.City]: 'city',
  [V1TimezoneSource.Device]: 'device',
  [V1TimezoneSource.Country]: 'country',
  [V1TimezoneSource.Phone]: 'phone',
}

// DEV-41: V1Currency приходит как 'CURRENCY_USD' / 'CURRENCY_VND' / 'CURRENCY_RUB'.
// CurrencyCode в домене — короткий 'USD' / 'VND' / 'RUB'.
function currencyFromAPI(v?: V1Currency | string): CurrencyCode | undefined {
  switch (v) {
    case V1Currency.Usd:
    case 'USD':
      return 'USD'
    case V1Currency.Vnd:
    case 'VND':
      return 'VND'
    case V1Currency.Rub:
    case 'RUB':
      return 'RUB'
    default:
      return undefined
  }
}

/**
 * Конвертирует дату из RFC3339 Timestamp формата в формат YYYY-MM-DD
 * @param timestamp - дата в формате RFC3339 (например, "1999-02-12T00:00:00Z")
 * @returns дата в формате YYYY-MM-DD (например, "1999-02-12") или undefined
 */
function convertTimestampToDate(timestamp?: string): string | undefined {
  if (!timestamp) return undefined
  // Извлекаем только дату из timestamp (первые 10 символов YYYY-MM-DD)
  return timestamp.split('T')[0]
}

/**
 * Конвертирует дату из формата YYYY-MM-DD в RFC3339 Timestamp формат
 * @param dateString - дата в формате YYYY-MM-DD (например, "1999-02-12")
 * @returns дата в формате RFC3339 (например, "1999-02-12T00:00:00Z") или undefined
 */
function convertDateToTimestamp(dateString?: string): string | undefined {
  if (!dateString) return undefined
  // Добавляем время в полночь по UTC
  return `${dateString}T00:00:00Z`
}

/**
 * Преобразовать Client из API модели в доменную модель
 */
export function mapClientFromAPI(dto: V1Client): Client {
  return {
    id: dto.id || '',
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,

    firstName: dto.firstName || '',
    lastName: dto.lastName || '',
    photoUrl: dto.photoUrl,
    dateOfBirth: convertTimestampToDate(dto.dateOfBirth),

    countryId: dto.countryId,
    country: dto.country as any,
    cityId: dto.cityId,
    cityName: dto.cityName,
    city: dto.city,
    timezone: dto.timezone || undefined,
    timezoneSource: TIMEZONE_SOURCES[dto.timezoneSource ?? ''],
    language: dto.language,

    contacts: (dto.contacts || []).map(mapContactFromAPI),
    preferredContactMethod: dto.preferredContactMethod as ContactType,

    leadSourceId: dto.leadSourceId,

    interestedPropertyTypes: dto.interestedPropertyTypes as any[],
    interestedLocations: dto.interestedLocations,
    budget: dto.budget
      ? {
          moneyFrom: dto.budget.moneyFrom,
          moneyTo: dto.budget.moneyTo,
          fromUsd: dto.budget.fromUsd,
          toUsd: dto.budget.toUsd,
          conversion:
            dto.budget.conversion && dto.budget.conversion.originalCurrency
              ? {
                  originalCurrency:
                    currencyFromAPI(dto.budget.conversion.originalCurrency) ?? 'USD',
                  originalFrom: dto.budget.conversion.originalFrom ?? 0,
                  originalTo: dto.budget.conversion.originalTo ?? 0,
                  exchangeRate: dto.budget.conversion.exchangeRate ?? 0,
                  convertedAt: dto.budget.conversion.convertedAt ?? '',
                }
              : undefined,
        }
      : undefined,
    purchaseGoal: dto.purchaseGoal as any,
    dealTimeline: dto.dealTimeline as any,
    downPaymentPercent: dto.downPaymentPercent,
    additionalCriteria: dto.additionalCriteria,

    firstContactDate: dto.firstContactDate,
    interactions: dto.interactions as any,
    sentProposals: dto.sentProposals,
    documentIds: dto.documentIds,
    managerNotes: dto.managerNotes as any,

    // current_deal_id удалён из client-data API (DEV-271/миграция 43); поле живёт только локально.
    responsibleManagerId: dto.responsibleManagerId,
    expectedCommission: dto.expectedCommission,
    paymentStatus: dto.paymentStatus as any,
    fundingType: dto.fundingType as any,

    isSubscribedToNewsletter: dto.isSubscribedToNewsletter,
    telegramBindingId: dto.telegramBindingId,
    origins: dto.origins as any,
    // DEV-1870: площадка лида. Бэкенд денормализует её из origins, потому что
    // список клиентов их не тянет, а колонке источника слаг нужен.
    landingSlug: (dto as { landingSlug?: string }).landingSlug,

    agentId: dto.agentId,

    // DEV-473 (§7.3). Агентский источник клиента.
    clientSourceType: dto.clientSourceType,
    companyId: dto.companyId,
    agentNameSnapshot: dto.agentNameSnapshot,
    companyNameSnapshot: dto.companyNameSnapshot,
    agentParticipationModel: dto.agentParticipationModel,
    agentClientRegisteredAt: dto.agentClientRegisteredAt,
    agentComment: dto.agentComment,
    agentDuplicateStatus: dto.agentDuplicateStatus,
    agentDuplicateReason: dto.agentDuplicateReason,
    // DEV-1942: заявки на дубль приезжают только в карточке (GetClient), в списке их нет.
    duplicateClaims: (dto as any).duplicateClaims ?? undefined,
    agentPanelClientRequestId: dto.agentPanelClientRequestId,

    isArchived: (dto as any).isArchived ?? false,
    archivedAt: (dto as any).archivedAt,
    // DEV-2172: приходит только в ответе CreateClient.
    revivedFromArchive: (dto as any).revivedFromArchive || undefined,
    archiveReason: (dto as any).archiveReason,
    // DEV-1061: причина архивации из справочника (id + read-only ключ/название).
    archiveReasonId: (dto as any).archiveReasonId,
    archiveReasonKey: (dto as any).archiveReasonKey || undefined,
    archiveReasonName: (dto as any).archiveReasonName || undefined,

    // Теги (DEV-232). Генерированный клиент ещё без модели Tag — берём из сырого ответа.
    tags: (dto as any).tags ?? undefined,
  }
}

/**
 * Преобразовать Contact из API модели
 */
function mapContactFromAPI(dto: V1Contact): Contact {
  return {
    type: dto.type as ContactType,
    value: dto.value || '',
  }
}

/**
 * Преобразовать Client в API модель (для создания/обновления)
 */
export function mapClientToAPI(client: Partial<Client>): Partial<V1Client> {
  return {
    firstName: client.firstName,
    lastName: client.lastName,
    photoUrl: client.photoUrl,
    dateOfBirth: convertDateToTimestamp(client.dateOfBirth),
    countryId: client.countryId,
    cityId: client.cityId,
    city: client.city,
    language: client.language,
    contacts: client.contacts?.map(mapContactToAPI),
    preferredContactMethod: client.preferredContactMethod as any,
    leadSourceId: client.leadSourceId,
    interestedPropertyTypes: client.interestedPropertyTypes as any[],
    interestedLocations: client.interestedLocations,
    budget: client.budget
      ? ({
          // DEV-301: каноника Money, legacy-поля удалены из API.
          moneyFrom: client.budget.moneyFrom,
          moneyTo: client.budget.moneyTo,
        } as any)
      : undefined,
    purchaseGoal: client.purchaseGoal as any,
    dealTimeline: client.dealTimeline as any,
    downPaymentPercent: client.downPaymentPercent,
    additionalCriteria: client.additionalCriteria,
    isSubscribedToNewsletter: client.isSubscribedToNewsletter,
    // agent_id выставляется только автоматически (agents-service при submit-лиде),
    // вручную в CRM не назначается — поэтому в payload не шлём.
  }
}

/**
 * Преобразовать Contact в API модель
 */
function mapContactToAPI(contact: Contact): V1Contact {
  return {
    type: contact.type as any,
    value: contact.value,
  }
}

/**
 * Получить полное имя клиента
 */
export function getClientFullName(client: Client): string {
  return `${client.firstName} ${client.lastName}`.trim()
}

/**
 * Город клиента для показа в списках и карточках.
 * DEV-851: справочник главнее ручного текста — как в формах и в cityPayload.
 * На легаси-записях заполнены оба поля, иначе список и форма покажут разные города.
 */
export function getClientDisplayCity(client: Client): string {
  return client.cityName || client.city || '—'
}

/**
 * Получить первичный контакт клиента
 */
export function getPrimaryContact(client: Client): Contact | undefined {
  return client.contacts[0]
}

/**
 * Получить email клиента
 */
export function getClientEmail(client: Client): string | undefined {
  return client.contacts.find((c) => c.type === ContactType.EMAIL)?.value
}

/**
 * Получить телефон клиента
 */
export function getClientPhone(client: Client): string | undefined {
  return client.contacts.find((c) => c.type === ContactType.PHONE)?.value
}

/**
 * Получить предпочитаемый контакт клиента
 */
export function getPreferredContact(client: Client): string | undefined {
  if (!client.preferredContactMethod || !client.contacts.length) {
    return getPrimaryContact(client)?.value
  }

  const contact = client.contacts.find((c) => c.type === client.preferredContactMethod)
  return contact?.value || getPrimaryContact(client)?.value
}

/**
 * Форматировать отображаемое имя клиента для селекта
 * Возвращает: "Firstname Lastname • contact"
 */
export function formatClientDisplayName(client: Client): string {
  const fullName = getClientFullName(client)
  const contact = getPreferredContact(client)

  if (!fullName && !contact) {
    return client.id
  }

  if (!contact) {
    return fullName || client.id
  }

  if (!fullName) {
    return contact
  }

  return `${fullName} • ${contact}`
}
